import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, Zap, ShieldCheck, Lock, CreditCard } from "lucide-react";

export default function Wallet() {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [history, setHistory] = useState([]);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/wallet/packs").then(({ data }) => setPacks(data.packs));
    if (user && typeof user === "object") {
      api.get("/payments/history").then(({ data }) => setHistory(data.transactions)).catch(() => {});
    }
  }, [user]);

  const buy = async (pack) => {
    if (!user || typeof user !== "object") {
      toast.error("Log in to buy tokens");
      return;
    }
    setBuying(pack.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: pack.id,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setBuying(null);
    }
  };

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter">
              Token Wallet
            </h1>
            <p className="text-zinc-500 mt-2">
              1 token = 1 bid ($0.50). Paid securely by Stripe — your card details never touch our servers.
            </p>
          </div>
          {user && typeof user === "object" && (
            <div className="card-surface px-6 py-4 flex items-center gap-4">
              <Coins className="text-[#FBBF24]" size={28} />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Your balance
                </div>
                <div
                  data-testid="wallet-balance"
                  className="font-mono-nums font-bold text-3xl text-[#FBBF24]"
                >
                  {user.tokens}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Trust row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
          <TrustTile icon={<ShieldCheck size={16} />} title="Stripe-secured" sub="PCI-DSS Level 1 payment partner" />
          <TrustTile icon={<Lock size={16} />} title="End-to-end encrypted" sub="256-bit TLS from browser to bank" />
          <TrustTile icon={<CreditCard size={16} />} title="Cards, Apple & Google Pay" sub="Checkout handles all methods" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {packs.map((pack) => (
            <div
              key={pack.id}
              data-testid={`pack-${pack.id}`}
              className="card-surface p-6 flex flex-col"
            >
              <div className="font-display font-extrabold text-xl capitalize tracking-tight mb-1">
                {pack.id}
              </div>
              <div className="font-mono-nums text-4xl font-bold text-[#FBBF24] mb-1">
                {pack.tokens}
              </div>
              <div className="text-xs text-zinc-500 mb-4">tokens</div>
              {pack.bonus > 0 && (
                <div className="chip chip-live mb-4 self-start">+{pack.bonus} bonus</div>
              )}
              <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between">
                <div className="font-mono-nums font-bold">${pack.price_usd}</div>
                <button
                  onClick={() => buy(pack)}
                  disabled={buying === pack.id || !user}
                  data-testid={`buy-pack-${pack.id}`}
                  className="btn-bid !py-2 !px-4 text-xs flex items-center gap-2"
                >
                  <Zap size={12} strokeWidth={3} />
                  {buying === pack.id ? "Opening…" : "Buy"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {!user && (
          <div className="mt-10 card-surface p-6 text-center">
            <p className="text-zinc-400">
              <Link to="/login" className="text-[#FBBF24] hover:underline">
                Log in
              </Link>{" "}
              or{" "}
              <Link to="/register" className="text-[#FBBF24] hover:underline">
                sign up
              </Link>{" "}
              to buy tokens.
            </p>
          </div>
        )}

        {/* Payment history */}
        {user && typeof user === "object" && history.length > 0 && (
          <div className="mt-16">
            <h2 className="font-display font-bold text-2xl tracking-tighter mb-4">
              Recent payments
            </h2>
            <div className="card-surface overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Pack</th>
                    <th className="py-3 px-4">Amount</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((t) => (
                    <tr key={t.session_id} className="border-t border-white/5">
                      <td className="py-3 px-4 font-mono-nums text-xs text-zinc-400">
                        {new Date(t.created_at).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 capitalize">{t.lookup_key}</td>
                      <td className="py-3 px-4 font-mono-nums">
                        ${(t.amount / 100).toFixed(2)}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`chip ${
                            t.payment_status === "paid" ? "chip-live" : "chip-closed"
                          }`}
                        >
                          {t.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TrustTile({ icon, title, sub }) {
  return (
    <div className="card-surface p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#1A1A24] border border-white/10 flex items-center justify-center text-[#22C55E]">
        {icon}
      </div>
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-[11px] text-zinc-500">{sub}</div>
      </div>
    </div>
  );
}
